import { cloneElement, useRef, useEffect, useState } from 'react';

import { CaretDown } from '@phosphor-icons/react';
import { formatCurrency } from '../../../shared/utils/general.util';

const AnnualPreviewDropdown = ({
	children,
	isOpen,
	toggleOpen,
	label,
	icon,
	total,
}: {
	children: React.ReactNode;
	isOpen: boolean;
	toggleOpen: () => void;
	label: string;
	icon: React.ReactNode;
	total?: number;
}) => {
	const contentRef = useRef<HTMLDivElement>(null);
	const [height, setHeight] = useState<number | undefined>(0);

	// Función para actualizar la altura del contenido
	const updateContentHeight = () => {
		if (isOpen && contentRef.current) {
			const contentHeight = contentRef.current.scrollHeight;
			setHeight(contentHeight);
		}
	};

	useEffect(() => {
		if (isOpen) {
			updateContentHeight();
		} else {
			setHeight(0);
		}
	}, [isOpen]);

	// Usar MutationObserver para detectar cambios en el contenido hijo
	useEffect(() => {
		if (!isOpen || !contentRef.current) return;

		// Configurar el observador para detectar cambios en el contenido
		const observer = new MutationObserver(updateContentHeight);

		// Observar cambios en el contenido y sus descendientes
		observer.observe(contentRef.current, {
			childList: true,
			subtree: true,
			attributes: true,
			characterData: true,
		});

		// Limpiar el observador cuando el componente se desmonte o se cierre
		return () => {
			observer.disconnect();
		};
	}, [isOpen]);

	// También podemos usar ResizeObserver para detectar cambios de tamaño directamente
	useEffect(() => {
		if (!isOpen || !contentRef.current) return;

		const resizeObserver = new ResizeObserver(updateContentHeight);
		resizeObserver.observe(contentRef.current);

		return () => {
			resizeObserver.disconnect();
		};
	}, [isOpen]);

	return (
		<div
			className={`bg-white rounded-xl px-4 ${isOpen ? 'pt-2 pb-4' : 'py-2'}`}
		>
			<button
				className='flex items-center justify-between w-full py-2'
				onClick={toggleOpen}
			>
				<p className='flex items-center gap-2 text-sm md:text-base'>
					{cloneElement(icon as React.ReactElement, {
						size: 20,
						weight: 'duotone',
						className: 'text-h-primary',
					})}
					{label}
				</p>
				<div className='flex items-center gap-2'>
					<p className='text-h-medium text-sm md:text-base'>
						{total ? formatCurrency(total) : '$0'}
					</p>
					<div
						className={`text-h-medium transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
					>
						<CaretDown size={16} />
					</div>
				</div>
			</button>
			<div
				className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'mt-2' : ''}`}
				style={{ height: height ? `${height}px` : '0px' }}
			>
				<div ref={contentRef}>
					<div>{children}</div>
				</div>
			</div>
		</div>
	);
};

export default AnnualPreviewDropdown;
